import Box from '@mui/material/Box';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { LookalikeSegmentSimilarityApi } from '@openx/types/segments';
import { useMemo } from 'react';
import { sortByPriority } from '../../../utils/sortByPriority';
import { LalReachBarGaugeItem } from './LalReachBarGaugeItem';

type LalReachBarGaugeProps = {
  baseReach: number;
  segmentSimilarities: LookalikeSegmentSimilarityApi[];
};

const MIN_SEED_WIDTH = '30px';

export const LalReachBarGauge = ({ baseReach, segmentSimilarities }: LalReachBarGaugeProps) => {
  const [highSimilarity, balancedSimilarity, scaledSimilarity] = useMemo(
    () => sortByPriority(segmentSimilarities),
    [segmentSimilarities]
  );

  const maxReach = baseReach + (scaledSimilarity?.segment?.reach ?? 0);

  const reachPercentage = (reach: number) => (reach / maxReach) * 100;

  const baseReachPercentage = reachPercentage(baseReach);
  const highSimilarityReachPercentage = reachPercentage(highSimilarity.segment?.reach ?? 0);
  const balancedSimilarityReachPercentage = reachPercentage(balancedSimilarity.segment?.reach ?? 0);
  const scaledSimilarityReachPercentage = reachPercentage(scaledSimilarity.segment?.reach ?? 0);

  const seedGaugeItemWidth = `max(${baseReachPercentage}%, ${MIN_SEED_WIDTH})`;

  const items = [
    {
      type: 'Seed',
      reach: baseReach,
      sx: {
        width: seedGaugeItemWidth,
        background: palette.charts.purple,
      },
    },
    {
      type: 'High',
      reach: highSimilarity.segment?.reach ?? 0,
      sx: {
        width: `${highSimilarityReachPercentage}%`,
        background: palette.charts.blue,
      },
    },
    {
      type: 'Balanced',
      reach: balancedSimilarity.segment?.reach ?? 0,
      sx: {
        width: `${balancedSimilarityReachPercentage}%`,
        background: palette.charts.lightBlue,
      },
    },
    {
      type: 'Scaled',
      reach: scaledSimilarity.segment?.reach ?? 0,
      sx: {
        width: `${scaledSimilarityReachPercentage}%`,
        background: palette.charts.canary,
      },
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        data-test="lal-reach-bar-gauge"
        sx={{
          position: 'relative',
          height: 19,
        }}
      >
        {items.map((item, index, arr) => (
          <LalReachBarGaugeItem
            key={item.type}
            sx={{
              zIndex: arr.length - index,
              left: item.type === 'Seed' ? 0 : `${seedGaugeItemWidth}`,
              maxWidth: item.type === 'Seed' ? 'none' : `calc(100% - ${seedGaugeItemWidth})`,
              ...item.sx,
            }}
            reach={item.reach}
            reachType={item.type}
          />
        ))}
      </Box>
    </Box>
  );
};
